import React, { useState, useRef, useEffect } from 'react';
import ChatMessage from './ChatMessage';

interface Message {
  role: string;
  content: string;
}

interface ChatInterfaceProps {
  messages: Message[];
  onSendMessage: (message: string, imageFile?: File) => void;
  isLoading?: boolean;
  documentQuotes?: string[];
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({
  messages,
  onSendMessage,
  isLoading = false,
  documentQuotes = []
}) => {
  const [input, setInput] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const endOfMessagesRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if ((input.trim() || imageFile) && !isLoading) {
      onSendMessage(input, imageFile || undefined);
      setInput('');
      setImageFile(null);
      setImagePreview(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      // Check file size (10MB limit)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        alert('Image file is too large. Please select an image smaller than 10MB.');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      
      setImageFile(file);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Scroll to bottom when new messages arrive or when the last message is updated (streaming)
  useEffect(() => {
    if (messages.length > 0 && endOfMessagesRef.current && messagesContainerRef.current) {
      // Try multiple methods to ensure scrolling works
      endOfMessagesRef.current.scrollIntoView({ behavior: 'smooth' });

      // Also try direct scrolling on the container
      const container = messagesContainerRef.current;
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [messages]);

  // Add scroll detection to show/hide scroll button and ensure proper wheel scrolling
  useEffect(() => {
    const messagesContainer = messagesContainerRef.current;
    if (!messagesContainer) return;

    const handleScroll = () => {
      if (!messagesContainer) return;

      // Check if user has scrolled up from bottom
      const isScrolledUp = messagesContainer.scrollHeight - messagesContainer.scrollTop - messagesContainer.clientHeight > 100;
      setShowScrollButton(isScrolledUp);
    };

    // Handle wheel events to ensure proper scrolling
    const handleWheel = (e: WheelEvent) => {
      e.stopPropagation(); // Prevent event bubbling
      // No need to manually scroll as the browser's default behavior will handle it
    };

    messagesContainer.addEventListener('scroll', handleScroll);
    messagesContainer.addEventListener('wheel', handleWheel as EventListener);

    return () => {
      messagesContainer.removeEventListener('scroll', handleScroll);
      messagesContainer.removeEventListener('wheel', handleWheel as EventListener);
    };
  }, []);

  // Function to scroll to bottom
  const scrollToBottom = () => {
    if (endOfMessagesRef.current) {
      endOfMessagesRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="overflow-hidden flex flex-col h-full" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div className="bg-gray-50 px-6 py-4 border-b border-gray-100 flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <img src="/lexa_chatbot_logo.png" alt="Lexa" className="h-8 w-8" />
          <span className="text-xl font-semibold text-gray-800">Lexa</span>
          <span className="text-xl font-semibold text-gray-400">•</span>
          <span className="text-xl font-semibold text-gray-800">AI agent</span>
        </div>
      </div>

      <div ref={messagesContainerRef} className="flex-1 px-6 py-4 overflow-y-auto bg-gray-50 relative" style={{ scrollbarWidth: 'thin', overflowX: 'hidden', flex: '1 1 auto', minHeight: '0px' }}>
        {messages.length === 0 ? (
          <div className="text-center text-gray-500 mt-8">
            <p>No messages yet. Start a conversation!</p>
          </div>
        ) : (
          <>
            {/*
              Store extracted quotes from each message so they persist across session updates.
              We use an IIFE (Immediately Invoked Function Expression) to process the messages
              and extract quotes once for each rendering.
            */}
            {(() => {
              // Create a map to store quotes for each message
              const messageQuotes = new Map();

              // Preprocess all messages to extract quotes
              messages.forEach((msg, idx) => {
                if (msg.role === 'assistant' && msg.content) {
                  const quotesRegex = /<quotes>([\s\S]*?)<\/quotes>/;
                  const quotesMatch = msg.content.match(quotesRegex);

                  if (quotesMatch) {
                    const quotesContent = quotesMatch[1];
                    const quotePattern = /<quote(?:\s+id=(\d+))?>([\s\S]*?)<\/quote>/g;
                    const extractedQuotes = [];
                    let match;

                    while ((match = quotePattern.exec(quotesContent)) !== null) {
                      const quoteText = match[2].trim();
                      if (quoteText) {
                        extractedQuotes.push(quoteText);
                      }
                    }

                    if (extractedQuotes.length > 0) {
                      messageQuotes.set(idx, extractedQuotes);
                    }
                  }
                }
              });

              // Use the most recent document quotes only for the last message
              if (messages.length > 0 && messages[messages.length - 1].role === 'assistant') {
                messageQuotes.set(messages.length - 1, documentQuotes);
              }

              // Render messages with their associated quotes
              return messages.map((message, index) => (
                <ChatMessage
                  key={index}
                  role={message.role}
                  content={message.content}
                  documentQuotes={messageQuotes.get(index) || []}
                  isTyping={message.role === 'assistant' && message.content === '...' && isLoading}
                />
              ));
            })()}
          </>
        )}
        <div ref={endOfMessagesRef} />

        {/* Scroll to bottom button */}
        {showScrollButton && (
          <button
            onClick={scrollToBottom}
            className="absolute bottom-4 right-4 bg-gray-800 text-white rounded-full p-2 shadow-lg hover:bg-gray-700 focus:outline-none"
            aria-label="Scroll to bottom"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </button>
        )}
      </div>
      
      <form onSubmit={handleSubmit} className="border-t border-gray-100 px-6 py-4 bg-gray-50 flex-shrink-0">
        <div className="max-w-4xl mx-auto">
          {/* Image preview */}
          {imagePreview && (
            <div className="mb-3 relative inline-block">
              <img 
                src={imagePreview} 
                alt="Attached image" 
                className="max-h-32 rounded-lg border border-gray-200"
              />
              <button
                type="button"
                onClick={removeImage}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}
          
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            {/* Text Input */}
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask anything"
              className="w-full px-4 py-3 rounded-t-lg focus:outline-none resize-none text-base"
              rows={3}
              disabled={isLoading}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
            />
            
            {/* Action Buttons Row */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
              <div className="flex items-center space-x-3">
                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                />
                
                {/* Attach Image Button */}
                <div className="relative group">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center justify-center w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors duration-200"
                    disabled={isLoading}
                  >
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </button>
                  
                  {/* Tooltip */}
                  <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                    Attach image
                  </div>
                </div>
              </div>

              {/* Send Button */}
              <button 
                type="submit"
                className="flex items-center justify-center w-10 h-10 bg-black hover:bg-gray-800 rounded-full transition-colors duration-200 disabled:opacity-50"
                disabled={(!input.trim() && !imageFile) || isLoading}
              >
                {isLoading ? (
                  <div className="animate-spin h-5 w-5">
                    <svg className="text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                ) : (
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"></path>
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ChatInterface;